import { supabase } from './supabaseClient';

export interface LookDeeperScenario {
  id: string;
  song_id: string;
  setup_text: string;
  choice_a_label: string;
  choice_a_outcome: string;
  choice_b_label: string;
  choice_b_outcome: string;
  most_people_miss: string;
  internal_source_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface LensOverride {
  id: string;
  song_id: string;
  lens_key: string;
  override_setup_text: string | null;
  override_choice_a_outcome: string | null;
  override_choice_b_outcome: string | null;
  override_most_people_miss: string | null;
  created_at: string;
  updated_at: string;
}

export interface LookDeeperScenarioWithOverrides {
  scenario: LookDeeperScenario;
  overrides: Record<string, LensOverride>;
}

export async function getExplorerLookDeeperScenario(
  songId: string
): Promise<{ data: LookDeeperScenarioWithOverrides | null; error: any }> {
  try {
    const { data: scenario, error: scenarioError } = await supabase
      .from('explorer_lookdeeper_scenarios')
      .select('*')
      .eq('song_id', songId)
      .maybeSingle();

    if (scenarioError) {
      return { data: null, error: scenarioError };
    }

    if (!scenario) {
      return { data: null, error: null };
    }

    const { data: overrides, error: overridesError } = await supabase
      .from('explorer_lookdeeper_lens_overrides')
      .select('*')
      .eq('song_id', songId);

    if (overridesError) {
      return { data: null, error: overridesError };
    }

    const overridesMap: Record<string, LensOverride> = {};
    if (overrides) {
      overrides.forEach((override) => {
        overridesMap[override.lens_key] = override;
      });
    }

    return {
      data: {
        scenario,
        overrides: overridesMap,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error };
  }
}

export interface UpsertScenarioPayload {
  song_id: string;
  setup_text: string;
  choice_a_label: string;
  choice_a_outcome: string;
  choice_b_label: string;
  choice_b_outcome: string;
  most_people_miss: string;
  internal_source_notes?: string | null;
  lens_override?: {
    lens_key: string;
    override_setup_text?: string | null;
    override_choice_a_outcome?: string | null;
    override_choice_b_outcome?: string | null;
    override_most_people_miss?: string | null;
  } | null;
}

export async function upsertExplorerLookDeeperScenario(
  payload: UpsertScenarioPayload
): Promise<{ data: LookDeeperScenarioWithOverrides | null; error: any }> {
  try {
    const {
      song_id,
      setup_text,
      choice_a_label,
      choice_a_outcome,
      choice_b_label,
      choice_b_outcome,
      most_people_miss,
      internal_source_notes,
      lens_override,
    } = payload;

    // Upsert base scenario
    const { data: scenario, error: scenarioError } = await supabase
      .from('explorer_lookdeeper_scenarios')
      .upsert(
        {
          song_id,
          setup_text,
          choice_a_label,
          choice_a_outcome,
          choice_b_label,
          choice_b_outcome,
          most_people_miss,
          internal_source_notes,
        },
        {
          onConflict: 'song_id',
        }
      )
      .select()
      .single();

    if (scenarioError) {
      return { data: null, error: scenarioError };
    }

    // Handle lens override if provided
    if (lens_override) {
      const hasAnyOverride =
        lens_override.override_setup_text ||
        lens_override.override_choice_a_outcome ||
        lens_override.override_choice_b_outcome ||
        lens_override.override_most_people_miss;

      if (hasAnyOverride) {
        const { error: overrideError } = await supabase
          .from('explorer_lookdeeper_lens_overrides')
          .upsert(
            {
              song_id,
              lens_key: lens_override.lens_key,
              override_setup_text: lens_override.override_setup_text,
              override_choice_a_outcome: lens_override.override_choice_a_outcome,
              override_choice_b_outcome: lens_override.override_choice_b_outcome,
              override_most_people_miss: lens_override.override_most_people_miss,
            },
            {
              onConflict: 'song_id,lens_key',
            }
          );

        if (overrideError) {
          console.error('Error upserting lens override:', overrideError);
        }
      } else {
        // Delete override if all fields are empty
        await supabase
          .from('explorer_lookdeeper_lens_overrides')
          .delete()
          .eq('song_id', song_id)
          .eq('lens_key', lens_override.lens_key);
      }
    }

    return await getExplorerLookDeeperScenario(song_id);
  } catch (error) {
    return { data: null, error };
  }
}

export async function deleteExplorerLookDeeperScenario(
  songId: string
): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('explorer_lookdeeper_scenarios')
      .delete()
      .eq('song_id', songId);

    return { error };
  } catch (error) {
    return { error };
  }
}
